import { Component, ojs, app, v, state, payload } from "modular-openscriptjs";
import { Capacitor } from "@capacitor/core";
import { eParse, either } from "../../helpers";
import { dom, Draggable as DraggableCore } from "../../fotastart";
import { $e } from "../../ojs.events";
import { gc } from "../../declarations";
import Draggable from "../Blocks/Draggable";

const h = app("h");
const broker = app("broker");

const WIDGET_ID = "voice-order-call-widget";

// Rotates through the pre-call prompt card while it's waiting for the
// user to tap "Start Call" - a quick "here's what you can ask Yoca"
// hint. Keep phrases short (the card is only 256px wide). Rendered by
// renderCapabilityRotator() below as a pure CSS carousel - see
// voice-order-capability-cycle in app.scss for how the per-phrase
// timing/keyframes are derived from this list's length.
const CAPABILITY_PHRASES = [
    "Order by voice",
    "Add custom items",
    "Pickup & drop off",
    "Buy AI credits",
    "Activate subscription",
    "Ask for help",
    "Navigate the app",
    "Cancel orders",
    "Deposit Capa",
];
const PHRASE_ROTATION_SECONDS = 2.4;
const CAPABILITY_CYCLE_SECONDS =
    CAPABILITY_PHRASES.length * PHRASE_ROTATION_SECONDS;

/**
 * The visual half of the voice "place order" feature. The actual call
 * plumbing (WebSocket, mic, billing) lives in
 * src/classes/VoiceOrderCallClient.js and is wired to the broker by
 * src/mediators/VoiceOrderCall.js - this component only ever talks to
 * that mediator over the broker.
 *
 * Renders as a persistent, draggable floating bubble (using the existing
 * Blocks/Draggable wrapper - snaps to the nearest screen edge/corner on
 * release, same as any other draggable chip in this app) rather than a
 * modal: the whole point is that the user can keep browsing, adding
 * items, checking other orders, etc. while the call is live, and just
 * drag the bubble out of the way. Nothing here ever blocks the rest of
 * the app - there is no backdrop and no bootstrap modal involved.
 *
 * Filed under components/Modals for now since that's where it was first
 * wired into AppOffCanvas.js - it is not, in fact, a modal.
 */
export default class VoiceOrderCallModal extends Component {
    status = state("idle"); // idle | prompt | connecting | ringing | active | ending | ended | error
    transcript = state([]); // [{ speaker: 'user' | 'agent', text }] - kept for a future transcript view, not rendered right now
    billedSeconds = state(0);
    creditsCharged = state(0);
    errorMessage = state(null);
    audioLevel = state(0); // 0-1, how loud the agent is right now (drives the pulse ring)
    minimized = state(false); // in-call only: full card vs. small square badge
    // Optimistic UI state for the loudspeaker/earpiece toggle - native
    // only (see VoiceOrderCallClient.setSpeakerphoneOn()'s docblock).
    // Starts false to match VoiceCallEngine's own earpiece-by-default
    // (isSpeakerphoneOn = false in requestAudioFocus()/
    // AVAudioSession's default routing).
    speakerOn = state(false);
    // The web/PWA build has no JS API to switch call audio between
    // earpiece and loudspeaker - this is native-only, the mirror image
    // of pipSupported below (which is web-only).
    speakerToggleSupported = Capacitor.isNativePlatform();

    // A live, second-by-second clock for the "time elapsed" display. Kept
    // separate from billedSeconds (which only jumps forward whenever a
    // heartbeat response comes back from the server, every
    // heartbeat_interval_seconds - accurate for billing, but visibly
    // "stuck" as a UI clock). This ticks locally off wall-clock time
    // instead, in its own v() so only this small text node re-renders
    // each second rather than the whole card.
    elapsedSeconds = state(0);

    endTimer = null;
    tickInterval = null;
    callStartedAt = null;

    // The open Document Picture-in-Picture window, if the user has
    // popped the call out - see enterPip() below. null the rest of the
    // time (including for the whole life of the widget when
    // pipSupported is false).
    pipWindow = null;
    // Document PiP is a browser/PWA-only affordance: native builds get
    // the same "tap to return" behavior for free from CallKit (iOS) /
    // the ongoing-call notification (Android) - see
    // packages/capacitor-voice-call - so this stays false on native
    // even in a WebView build new enough to expose the API.
    pipSupported =
        !Capacitor.isNativePlatform() &&
        typeof documentPictureInPicture !== "undefined";

    // A SECOND, separate Picture-in-Picture mechanism from pipWindow
    // above - see setupAutoPip()'s docblock for why. Unlike pipWindow
    // (opened only when the user taps "pop out"), this one is armed for
    // the whole call and the browser itself decides when to actually
    // show the floating window - the moment the tab is hidden/switched
    // away from, with no click needed at that moment at all. `_autoPip*`
    // fields hold the offscreen canvas/video/MediaStream plumbing that
    // makes that possible; null/false whenever no call is active or the
    // browser doesn't support the trick (autoPipSupported).
    autoPipSupported =
        !Capacitor.isNativePlatform() &&
        typeof HTMLVideoElement !== "undefined" &&
        "autoPictureInPicture" in HTMLVideoElement.prototype &&
        "mediaSession" in navigator;
    _autoPipCanvas = null;
    _autoPipCtx = null;
    _autoPipVideo = null;

    render(...args) {
        // The voice ordering call icon only makes sense for a signed-in
        // user (it drives cart/orders/billing, none of which exist for
        // a guest) - render nothing at all when gc.auth is false, for
        // every state of the widget (idle trigger, prompt card, active
        // call card alike), not just the initial trigger button.
        return v(gc.auth, (auth) =>
            either(
                auth.value,
                () =>
                    h.Draggable(
                        {
                            id: WIDGET_ID,
                            // Starts docked at the middle of the right edge rather
                            // than the bottom-right corner, where it's more likely
                            // to sit over bottom navigation/action bars.
                            options: { snap: true, initialSnap: "center-right" },
                        },
                        this.renderBubble(),
                        ...args,
                    ),
                h.empty(),
            ),
        );
    }

    renderBubble() {
        return h.div(
            { class: "voice-order-call-bubble" },
            v(this.status, (status) => {
                if (status.value === "idle") return this.renderTrigger();
                if (status.value === "prompt") return this.renderPromptCard();

                return v(this.minimized, (min) =>
                    min.value
                        ? this.renderMinimized(status.value)
                        : this.renderCallCard(status.value),
                );
            }),
        );
    }

    renderTrigger() {
        return h.button(
            {
                class: "btn btn-fab rounded-circle text-white d-flex align-items-center justify-content-center",
                style: "background: linear-gradient(135deg, #34d399 0%, #059669 100%);",
                type: "button",
                title: "Order by voice",
                listeners: {
                    click: () => this.openPrompt(),
                },
            },
            h.i({ class: "bi bi-telephone-fill fs-5" }),
        );
    }

    /**
     * The light, animated "Call to Order" card shown after tapping the
     * trigger - nothing has started yet (no reservation, no socket).
     * Expanded by default (no collapsed variant): the only actions are
     * tap the green phone to actually start the call, or dismiss.
     */
    renderPromptCard() {
        return h.div(
            {
                // 16:9, small - 256x144.
                class: "voice-order-prompt-card rounded-4 shadow-lg d-flex flex-column position-relative",
                style: "width: 256px; height: 144px; padding: 10px 12px;",
            },
            // Dismiss - floated in the corner so the title can sit centered.
            h.button(
                {
                    class: "btn btn-sm btn-link text-dark opacity-75 p-0 lh-1 position-absolute",
                    style: "top: 8px; right: 10px;",
                    type: "button",
                    title: "Dismiss",
                    listeners: { click: () => this.closePrompt() },
                },
                h.i({ class: "fas fa-times", style: "font-size: 0.75rem;" }),
            ),
            // Title - centered, a bit bolder.
            h.div(
                {
                    class: "fw-bold text-dark text-center",
                    style: "font-size: 0.8rem;",
                },
                "Talk to YoCA",
            ),
            // Rotating "what you can ask Yoca" hint.
            this.renderCapabilityRotator(),
            // Phone button + label, vertically centered in the remaining space.
            h.div(
                {
                    class: "flex-fill d-flex flex-column align-items-center justify-content-center gap-1",
                },
                h.button(
                    {
                        class: "voice-order-pulse-btn btn rounded-circle d-flex align-items-center justify-content-center shadow border-0 text-white",
                        style: "width: 52px; height: 52px; background: linear-gradient(135deg, #34d399 0%, #059669 100%);",
                        type: "button",
                        title: "Start Call",
                        listeners: { click: () => this.startCall() },
                    },
                    h.i({ class: "bi bi-telephone-fill", style: "font-size: 1.1rem;" }),
                ),
                h.div(
                    {
                        class: "fw-semibold text-dark",
                        style: "font-size: 0.65rem;",
                    },
                    "Start Call",
                ),
            ),
            // Watermark - no background, tucked in the bottom-right corner.
            h.div(
                {
                    class: "text-end",
                    style: "font-size: 0.55rem; opacity: 0.5;",
                },
                h.i({ class: "bi bi-stars me-1" }),
                "AI powered",
            ),
        );
    }

    /**
     * The rotating "Order by voice / Add custom items / ..." hint in the
     * prompt card. Every phrase in CAPABILITY_PHRASES renders at once,
     * absolutely stacked on top of each other, and takes its turn purely
     * through a staggered CSS animation-delay (voice-order-capability-cycle
     * in app.scss) - so it loops on its own for as long as the card is on
     * screen, with no interval/state to start or tear down.
     */
    renderCapabilityRotator() {
        return h.div(
            { class: "voice-order-capability-rotator" },
            ...CAPABILITY_PHRASES.map((phrase, i) =>
                h.div(
                    {
                        class: "voice-order-capability-item text-dark",
                        style:
                            "font-size: 0.58rem; opacity: 0.65; " +
                            `animation-duration: ${CAPABILITY_CYCLE_SECONDS}s; ` +
                            `animation-delay: ${(i * PHRASE_ROTATION_SECONDS).toFixed(2)}s;`,
                    },
                    phrase,
                ),
            ),
        );
    }

    openPrompt() {
        this.status.value = "prompt";
        this.resnap();
    }

    closePrompt() {
        this.status.value = "idle";
        this.resnap();
    }

    /**
     * The in-call view - same small light 16:9 card as the prompt, just
     * with a red "End Call" and a ring around it that pulses with the
     * agent's actual voice level (see audioLevel, fed by
     * VoiceOrderCallClient's playback analyser). Reused for the brief
     * ended/error summary too, swapping the button for a neutral close.
     */
    renderCallCard(status) {
        const isLive = ["connecting", "ringing", "active", "ending"].includes(status);

        return h.div(
            {
                class: "voice-order-prompt-card rounded-4 shadow-lg d-flex flex-column position-relative",
                style: "width: 256px; height: 144px; padding: 10px 12px;",
            },
            // Minimize while live, otherwise just close - floated in the
            // corner so the title can stay centered.
            h.button(
                {
                    class: "btn btn-sm btn-link text-dark opacity-75 p-0 lh-1 position-absolute",
                    style: "top: 8px; right: 10px;",
                    type: "button",
                    title: isLive ? "Minimize" : "Close",
                    listeners: {
                        click: () =>
                            isLive ? this.minimize() : this.closeBubble(),
                    },
                },
                h.i({
                    class: isLive ? "bi bi-dash-square" : "fas fa-times",
                    style: "font-size: 0.75rem;",
                }),
            ),
            // Pop out into a small always-on-top window (Document
            // Picture-in-Picture) so the user can switch tabs/apps and
            // still see + end the call - see enterPip()/togglePip()
            // below. Native builds never show this (pipSupported is
            // false there); they get the equivalent affordance for free
            // from CallKit/the foreground-service notification instead.
            isLive && this.pipSupported
                ? h.button(
                      {
                          class: "btn btn-sm btn-link text-dark opacity-75 p-0 lh-1 position-absolute",
                          style: "top: 8px; left: 10px;",
                          type: "button",
                          title: this.pipWindow
                              ? "Back to full view"
                              : "Pop out (Picture-in-Picture)",
                          listeners: { click: () => this.togglePip() },
                      },
                      h.i({
                          class: "bi bi-box-arrow-up-right",
                          style: "font-size: 0.75rem;",
                      }),
                  )
                : isLive && this.speakerToggleSupported
                  ? v(this.speakerOn, (so) =>
                        h.button(
                            {
                                class: "btn btn-sm btn-link text-dark opacity-75 p-0 lh-1 position-absolute",
                                style: "top: 8px; left: 10px;",
                                type: "button",
                                title: so.value
                                    ? "Switch to earpiece"
                                    : "Switch to loudspeaker",
                                listeners: { click: () => this.toggleSpeaker() },
                            },
                            h.i({
                                class: so.value
                                    ? "bi bi-volume-up-fill"
                                    : "bi bi-volume-down",
                                style: "font-size: 0.75rem;",
                            }),
                        ),
                    )
                  : h.empty(),
            // Title + subtitle (elapsed time while live, the error
            // message when there is one), centered.
            h.div(
                { class: "text-center" },
                h.div(
                    {
                        class: "fw-bold text-dark",
                        style: "font-size: 0.8rem;",
                    },
                    this.statusLabel(status),
                ),
                v(this.errorMessage, (msg) =>
                    msg.value
                        ? h.div(
                              {
                                  class: "text-danger text-truncate",
                                  style: "font-size: 0.65rem;",
                              },
                              msg.value,
                          )
                        : v(this.elapsedSeconds, (es) =>
                              h.div(
                                  {
                                      class: "text-muted",
                                      style: "font-size: 0.65rem;",
                                  },
                                  this.formatDuration(es.value),
                              ),
                          ),
                ),
            ),
            // Ring (audio-reactive while live) + phone button.
            h.div(
                {
                    class: "flex-fill d-flex flex-column align-items-center justify-content-center gap-1",
                },
                h.div(
                    { class: "position-relative", style: "width: 52px; height: 52px;" },
                    isLive
                        ? v(this.audioLevel, (level) =>
                              this.renderVoiceRing(level.value),
                          )
                        : h.empty(),
                    h.button(
                        {
                            class: "btn rounded-circle d-flex align-items-center justify-content-center shadow position-relative",
                            style: isLive
                                ? "width: 52px; height: 52px; z-index: 1; color: #f87171; background: #ffffff; border: 2px solid #f87171;"
                                : "width: 52px; height: 52px; z-index: 1; color: #3b82f6; background: #ffffff; border: 2px solid #3b82f6;",
                            type: "button",
                            title: isLive ? "End Call" : "Close",
                            listeners: {
                                click: () =>
                                    isLive ? this.endCall() : this.closeBubble(),
                            },
                        },
                        h.i({
                            class: isLive
                                ? "bi bi-telephone-fill"
                                : "fas fa-check",
                            style: "font-size: 1.1rem;",
                        }),
                    ),
                ),
                h.div(
                    {
                        class: "fw-semibold text-dark",
                        style: "font-size: 0.65rem;",
                    },
                    isLive ? "End Call" : "Close",
                ),
            ),
            // Watermark - credits used, no background, bottom-right.
            v(this.creditsCharged, (cc) =>
                h.div(
                    {
                        class: "text-end",
                        style: "font-size: 0.55rem; opacity: 0.5;",
                    },
                    `${cc.value} AI credits used`,
                ),
            ),
        );
    }

    /**
     * A soft ring (Carata brand purple) around the phone button that
     * grows/fades with how loud the agent is speaking right now
     * (0 = resting, 1 = loud). Kept purple rather than red so it never
     * reads as an error/alert - the end-call button is the only red
     * element in this card.
     */
    renderVoiceRing(level) {
        const scale = (1 + level * 0.9).toFixed(2);
        const opacity = (0.15 + level * 0.45).toFixed(2);

        return h.div({
            class: "position-absolute rounded-circle",
            style: `inset: 0; background: rgba(151, 62, 135, ${opacity}); transform: scale(${scale}); transition: transform 80ms linear, opacity 80ms linear; pointer-events: none;`,
        });
    }

    /**
     * Minimized in-call badge - a small green square (not a circle, so
     * it reads as distinct from the idle trigger) showing just the
     * elapsed time. Tap to bring the full card back.
     */
    renderMinimized(status) {
        return h.button(
            {
                class: "btn text-white rounded-3 d-flex flex-column align-items-center justify-content-center shadow-lg border-0 gap-0",
                style: "width: 52px; height: 52px; background: linear-gradient(135deg, #34d399 0%, #059669 100%);",
                type: "button",
                title: "Call in progress - tap to expand",
                listeners: { click: () => this.expand() },
            },
            h.i({ class: "bi bi-telephone-fill", style: "font-size: 0.8rem;" }),
            v(this.elapsedSeconds, (es) =>
                h.div(
                    { style: "font-size: 0.6rem; line-height: 1.2;" },
                    this.formatDuration(es.value),
                ),
            ),
        );
    }

    minimize() {
        this.minimized.value = true;
        this.resnap();
    }

    expand() {
        this.minimized.value = false;
        this.resnap();
    }

    togglePip() {
        if (this.pipWindow) {
            this.exitPip();
        } else {
            this.enterPip();
        }
    }

    /**
     * Opens a small, separate always-on-top window (the Document
     * Picture-in-Picture API) mirroring the live call's status/timer/
     * End Call button, so a user who switches tabs or minimizes the
     * browser - the one background scenario the floating in-page bubble
     * above can't survive, since a backgrounded tab is exactly what
     * throttles this component's own timers/rendering - still has
     * something on screen to check on and end the call from. Keeping
     * that window open is also, incidentally, believed to keep this
     * page's own JS from being throttled while backgrounded (browsers
     * generally treat a page with an open PiP window as still "in use"),
     * though that's a side effect of the platform, not something this
     * code relies on directly - the PiP window's own End Call button
     * works regardless.
     *
     * Must be called from a real user gesture (a click - see
     * togglePip()'s listener) - browsers require "transient activation"
     * to open one at all, which is exactly why this is a button the
     * user taps before switching away, rather than something fired
     * automatically off a visibilitychange/backgrounding event.
     */
    async enterPip() {
        if (!this.pipSupported || this.pipWindow) return;

        try {
            const pip = await documentPictureInPicture.requestWindow({
                width: 260,
                height: 150,
            });

            this.pipWindow = pip;
            this.buildPipDocument(pip);
            broker.send(
                $e.voiceOrderCall.needs.pipStateChanged,
                payload({ active: true }),
            );

            // The user can close the PiP window itself (its own native
            // chrome has a close button) without ever tapping our End
            // Call button inside it - notice that and just drop the
            // reference so togglePip()/the card UI fall back to
            // offering to pop out again, rather than the call itself.
            // Also tells the client PiP is gone - see the docblock above
            // this.pipWindow's field declaration - so a subsequent
            // hidden tab goes back to the normal 30s auto-hangup.
            pip.addEventListener(
                "pagehide",
                () => {
                    this.pipWindow = null;
                    broker.send(
                        $e.voiceOrderCall.needs.pipStateChanged,
                        payload({ active: false }),
                    );
                    this.resnap();
                },
                { once: true },
            );
        } catch (e) {
            // Most likely cause: called without a fresh-enough user
            // gesture, or the browser advertised the API but declined
            // anyway. Nothing actionable to show the user for this -
            // they simply stay on the normal in-page bubble, same as
            // before this feature existed.
            console.warn(
                "[VoiceOrderCallModal] Could not open Picture-in-Picture:",
                e,
            );
        }
    }

    /**
     * Builds the PiP window's entire (tiny, dependency-free) document
     * from scratch rather than copying this app's stylesheet/icon-font
     * links into it - the PiP window is a real separate document, and
     * pulling in the app's full CSS bundle just for four lines of text
     * and one button isn't worth the load time or the risk of it not
     * resolving relative asset URLs correctly from inside that window.
     */
    buildPipDocument(pip) {
        const doc = pip.document;
        doc.title = "Yoca call";

        const style = doc.createElement("style");
        style.textContent = `
            body { margin: 0; height: 100vh; box-sizing: border-box; padding: 12px;
                display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px;
                background: #111827; color: #fff;
                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; text-align: center; }
            .pip-status { font-size: 0.85rem; font-weight: 600; }
            .pip-timer { font-size: 0.7rem; opacity: 0.7; }
            .pip-end-btn { width: 44px; height: 44px; border-radius: 50%; border: 2px solid #f87171;
                background: #ffffff; color: #f87171; display: flex; align-items: center; justify-content: center;
                font-size: 1.1rem; cursor: pointer; }
            .pip-hint { font-size: 0.6rem; opacity: 0.5; }
        `;
        doc.head.appendChild(style);

        const statusEl = doc.createElement("div");
        statusEl.className = "pip-status";
        statusEl.id = "pip-status";
        statusEl.textContent = this.statusLabel(this.status.value);

        const timerEl = doc.createElement("div");
        timerEl.className = "pip-timer";
        timerEl.id = "pip-timer";
        timerEl.textContent = this.formatDuration(this.elapsedSeconds.value);

        const endBtn = doc.createElement("button");
        endBtn.className = "pip-end-btn";
        endBtn.title = "End Call";
        // No icon font is loaded in this standalone document - a plain
        // glyph keeps it dependency-free.
        endBtn.textContent = "\u260E";
        endBtn.addEventListener("click", () => this.endCall());

        const hint = doc.createElement("div");
        hint.className = "pip-hint";
        hint.textContent = "Switch back to the app for the full view";

        doc.body.append(statusEl, timerEl, endBtn, hint);
    }

    /**
     * Keeps the PiP window's status/timer text in sync with this
     * component's own state - called from the same two places that
     * already change status.value/elapsedSeconds.value (the
     * statusChanged handler and the ticker interval below) rather than
     * via a reactive v() binding, since the PiP window's DOM lives
     * outside this component's own render tree entirely. A no-op
     * whenever no PiP window is open.
     */
    updatePipContent() {
        if (!this.pipWindow) return;

        const doc = this.pipWindow.document;
        const statusEl = doc.getElementById("pip-status");
        const timerEl = doc.getElementById("pip-timer");

        if (statusEl) statusEl.textContent = this.statusLabel(this.status.value);
        if (timerEl) timerEl.textContent = this.formatDuration(this.elapsedSeconds.value);
    }

    exitPip() {
        if (!this.pipWindow) return;

        try {
            this.pipWindow.close();
        } catch (e) {
            /* best effort */
        }
        this.pipWindow = null;
        broker.send(
            $e.voiceOrderCall.needs.pipStateChanged,
            payload({ active: false }),
        );
    }

    /**
     * Arms the browser's own AUTOMATIC video Picture-in-Picture for this
     * call, so switching tabs pops a small floating window up with no
     * click needed at that moment - unlike enterPip() above (Document
     * Picture-in-Picture), which can only ever open from a direct click,
     * by hard browser restriction (see the chat discussion this came
     * from - there is no way to pre-authorize or auto-trigger Document
     * PiP; it isn't a permission, it's a per-call gesture check).
     *
     * The trick (the same one video-calling sites use): a `<video>`
     * element with the `autoPictureInPicture` property set is put into
     * PiP automatically BY THE BROWSER ITSELF - not by a script call -
     * the moment its tab is hidden/occluded, and brought back
     * automatically when it's visible again. That only works for actual
     * video content, so this feeds it a muted stream of a small canvas
     * we redraw with the call status/timer (see drawAutoPipFrame()) via
     * canvas.captureStream() - there's nothing to see in the tab itself,
     * this canvas/video pair never gets inserted where the user would
     * normally look, it exists purely to be a legal source for the
     * browser's auto-PiP feature.
     *
     * A video PiP window's controls are a fixed, limited vocabulary
     * (Media Session API actions) - no custom HTML buttons - so the
     * "hangup" action (added to that API specifically for calling apps)
     * is registered here to end the call from inside it. This is
     * deliberately kept SEPARATE from enterPip()/pipWindow above rather
     * than replacing it: this auto-triggered window can only ever show
     * that fixed hangup control, while the Document PiP window still
     * offers a richer custom view for anyone who taps "pop out"
     * themselves - each covers a case the other can't.
     *
     * No-op entirely when autoPipSupported is false (most non-Chromium
     * browsers today, and native builds - see that field's docblock).
     */
    setupAutoPip() {
        if (!this.autoPipSupported || this._autoPipVideo) return;

        try {
            const canvas = document.createElement("canvas");
            canvas.width = 320;
            canvas.height = 180;
            const ctx = canvas.getContext("2d");

            const video = document.createElement("video");
            video.muted = true;
            video.playsInline = true;
            video.autoPictureInPicture = true;
            // Present in the document (auto-PiP eligibility is tied to
            // the browser having actually displayed/occluded this
            // element - a fully detached element doesn't qualify) but
            // visually inert - this is never something the user should
            // notice in the page itself, only as the floating window
            // the browser produces from it once they switch away.
            video.style.cssText =
                "position:fixed; left:-9999px; top:-9999px; width:2px; height:2px; opacity:0.01; pointer-events:none;";
            document.body.appendChild(video);

            video.srcObject = canvas.captureStream(1);

            this._autoPipCanvas = canvas;
            this._autoPipCtx = ctx;
            this._autoPipVideo = video;

            if ("mediaSession" in navigator) {
                navigator.mediaSession.metadata = new MediaMetadata({
                    title: "Call with Yoca",
                    artist: "Carata",
                });
                navigator.mediaSession.setActionHandler("hangup", () =>
                    this.endCall(),
                );
            }

            this.drawAutoPipFrame();
            video.play().catch(() => {
                /* best effort - a rejected muted autoplay just means no
                   auto-PiP is available this call, nothing else breaks */
            });
        } catch (e) {
            // Feature-detected above, but still best-effort - any
            // failure here should never affect the call itself.
            console.warn(
                "[VoiceOrderCallModal] Could not arm automatic Picture-in-Picture:",
                e,
            );
            this.teardownAutoPip();
        }
    }

    teardownAutoPip() {
        if ("mediaSession" in navigator) {
            try {
                navigator.mediaSession.setActionHandler("hangup", null);
                navigator.mediaSession.metadata = null;
            } catch (e) {
                /* best effort */
            }
        }

        if (this._autoPipVideo) {
            try {
                this._autoPipVideo.pause();
                this._autoPipVideo.srcObject = null;
                this._autoPipVideo.remove();
            } catch (e) {
                /* best effort */
            }
        }

        this._autoPipVideo = null;
        this._autoPipCanvas = null;
        this._autoPipCtx = null;
    }

    /**
     * Redraws the offscreen canvas setupAutoPip() feeds into the
     * auto-PiP video - called from the same two places updatePipContent()
     * already is (the statusChanged handler and the ticker interval), so
     * both PiP mechanisms stay in sync off the same state changes.
     */
    drawAutoPipFrame() {
        const ctx = this._autoPipCtx;
        if (!ctx) return;

        const canvas = this._autoPipCanvas;
        ctx.fillStyle = "#111827";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.fillStyle = "#ffffff";
        ctx.textAlign = "center";
        ctx.font = "600 20px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
        ctx.fillText(this.statusLabel(this.status.value), canvas.width / 2, 84);

        ctx.fillStyle = "rgba(255,255,255,0.7)";
        ctx.font = "400 15px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
        ctx.fillText(
            this.formatDuration(this.elapsedSeconds.value),
            canvas.width / 2,
            114,
        );
    }

    statusLabel(status) {
        return (
            {
                connecting: "Connecting...",
                ringing: "Yoca is picking up...",
                active: "Call Ongoing",
                ending: "Ending call...",
                ended: "Call ended",
                error: "Call error",
            }[status] ?? "Talk to Yoca"
        );
    }

    formatDuration(totalSeconds) {
        const seconds = Math.max(0, totalSeconds ?? 0);
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${String(s).padStart(2, "0")}`;
    }

    startCall() {
        this.setupAutoPip();
        this.transcript.value = [];
        this.billedSeconds.value = 0;
        this.creditsCharged.value = 0;
        this.errorMessage.value = null;
        this.audioLevel.value = 0;
        this.elapsedSeconds.value = 0;
        this.minimized.value = false;
        this.speakerOn.value = false;
        this.status.value = "connecting";
        this.resnap();

        broker.send($e.voiceOrderCall.needs.start, payload({}));
    }

    toggleSpeaker() {
        this.speakerOn.value = !this.speakerOn.value;
        broker.send(
            $e.voiceOrderCall.needs.setSpeakerphoneOn,
            payload({ enabled: this.speakerOn.value }),
        );
    }

    endCall() {
        this.status.value = "ending";
        broker.send($e.voiceOrderCall.needs.end, payload({}));
    }

    closeBubble() {
        this.exitPip();
        this.teardownAutoPip();
        this.clearEndTimer();
        this.stopTicker();
        this.status.value = "idle";
        this.minimized.value = false;
        this.resnap();
    }

    /**
     * Re-run the drag helper's own edge-snap after the bubble's content
     * (and therefore its size) changes, so switching between the idle
     * trigger, the prompt/call cards, and the minimized badge never
     * leaves part of it hanging off the screen.
     */
    resnap() {
        requestAnimationFrame(() => {
            const el = dom.id(WIDGET_ID);
            const instance = el && DraggableCore.instances.get(el);
            instance?.snap();
        });
    }

    clearEndTimer() {
        if (this.endTimer) {
            clearTimeout(this.endTimer);
            this.endTimer = null;
        }
    }

    /**
     * Start the local, second-by-second "time elapsed" clock. Driven off
     * wall-clock time (Date.now()) rather than counting up from
     * billedSeconds, so it ticks smoothly instead of jumping once per
     * heartbeat_interval_seconds.
     */
    startTicker() {
        if (this.tickInterval) return;
        this.callStartedAt = Date.now();
        this.elapsedSeconds.value = 0;
        this.tickInterval = setInterval(() => {
            this.elapsedSeconds.value = Math.floor(
                (Date.now() - this.callStartedAt) / 1000,
            );
            this.updatePipContent();
            this.drawAutoPipFrame();
        }, 1000);
    }

    stopTicker() {
        if (this.tickInterval) {
            clearInterval(this.tickInterval);
            this.tickInterval = null;
        }
    }

    /**
     * After the call ends (cleanly or on error), leave the summary up
     * briefly so the user can see the final time/credits, then fold
     * back down to the idle mic trigger on its own.
     */
    scheduleAutoClose() {
        this.clearEndTimer();
        this.endTimer = setTimeout(() => this.closeBubble(), 5000);
    }

    $$voiceOrderCall = {
        has: {
            statusChanged: (ed) => {
                const { message } = eParse(ed);
                const status = message.get("status");
                if (status) this.status.value = status;
                if (status === "active") this.startTicker();
                this.updatePipContent();
                this.drawAutoPipFrame();
                this.resnap();
            },

            transcript: (ed) => {
                const { message } = eParse(ed);
                const text = message.get("text");
                const speaker = message.get("speaker", "agent");
                if (!text) return;

                this.transcript.value = [
                    ...this.transcript.value,
                    { speaker, text },
                ];
            },

            heartbeat: (ed) => {
                const { message } = eParse(ed);
                this.billedSeconds.value = message.get(
                    "billedSeconds",
                    this.billedSeconds.value,
                );
                this.creditsCharged.value = message.get(
                    "creditsCharged",
                    this.creditsCharged.value,
                );
            },

            audioLevel: (ed) => {
                const { message } = eParse(ed);
                this.audioLevel.value = message.get(
                    "level",
                    this.audioLevel.value,
                );
            },

            // The native proximity sensor forced the speakerphone off
            // (phone brought to the ear while on loudspeaker) - keep the
            // toggle button's icon/title in sync with what actually
            // happened rather than the last tap the user made.
            speakerphoneChanged: (ed) => {
                const { message } = eParse(ed);
                this.speakerOn.value = !!message.get(
                    "enabled",
                    this.speakerOn.value,
                );
            },

            ended: (ed) => {
                const { message } = eParse(ed);
                this.billedSeconds.value = message.get(
                    "billedSeconds",
                    this.billedSeconds.value,
                );
                this.creditsCharged.value = message.get(
                    "creditsCharged",
                    this.creditsCharged.value,
                );
                this.audioLevel.value = 0;
                this.minimized.value = false;
                this.stopTicker();
                this.exitPip();
                this.teardownAutoPip();
                this.status.value = "ended";
                this.resnap();
                this.scheduleAutoClose();
            },

            error: (ed) => {
                const { message } = eParse(ed);
                this.errorMessage.value = message.get(
                    "message",
                    "The voice call ran into a problem.",
                );
                this.audioLevel.value = 0;
                this.minimized.value = false;
                this.stopTicker();
                this.exitPip();
                this.teardownAutoPip();
                this.status.value = "error";
                this.resnap();
                this.scheduleAutoClose();
            },
        },
    };

    cleanUp() {
        super.cleanUp();
        this.clearEndTimer();
        this.stopTicker();
        this.exitPip();
        this.teardownAutoPip();
    }
}

ojs(VoiceOrderCallModal, Draggable);
